import { FormikTouched, FormikHelpers, FormikProps } from 'formik'
import { useEffect, useRef, useState } from 'react'
import { fetchBusinessData } from 'src/services/business-data'
import { ISubscribeFields } from '.'
import { fetchAllSubscriptionsComplete, Subscription } from 'src/services/subscribe'
import { getJwtToken } from 'src/utils/session'

const useBusinessDataAutoFill = (
  values: ISubscribeFields,
  touched: FormikTouched<ISubscribeFields>,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  enabled: boolean,
  formik?: FormikHelpers<ISubscribeFields>
) => {
  const [pending, setPending] = useState(false)
  const [clientExists, setClientExists] = useState(false)
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [errorEllipro, setErrorEllipro] = useState(false)

  const loadBusinessData = async (businessCode: string, country: 'FR' | 'BE') => {
    setPending(true)
    setErrorEllipro(false)

    try {
      const response = await fetchBusinessData(businessCode)

      if (response.code !== 200 || !response.data) {
        setErrorEllipro(true)
        throw new Error()
      }

      const result = response.data

      const assignValue = (field: string, value: any) => {
        if (value) {
          setFieldValue(field, value)
        }
      }

      assignValue('name', result.name)

      if (country === 'BE') {
        assignValue('naf', result.activityCode)
      } else {
        assignValue('ape', result.activityCode)
      }

      setFieldValue('job', '')
      setFieldValue('firstname', '')
      setFieldValue('lastname', '')
      setFieldValue('managers', [])

      if (result.managers.length > 0) {
        const processedManagers = result.managers.map((manager) => ({
          firstname: manager.firstname || '',
          lastname: manager.lastname || '',
          position: manager.position || '',
        }))

        assignValue('managers', processedManagers)

        assignValue('job', processedManagers[0].position || '')
        assignValue('firstname', processedManagers[0].firstname || '')
        assignValue('lastname', processedManagers[0].lastname || '')

        if (processedManagers.length === 1) {
          setFieldValue('isSeveralManagers', false)
        } else {
          setFieldValue('isSeveralManagers', true)
        }
      }

      assignValue('businessDescription', result.activity)
      assignValue('address', result.address)
      assignValue('addressNumber', result.addressNumber)
      assignValue('city', result.city)
      assignValue('zipCode', result.zipCode)
      assignValue('phone', result.phone)
      assignValue('email', result.email)
    } catch (error) {
      console.log({ error })
    }

    setPending(false)
  }
  const userLoggedToken = getJwtToken()

  useEffect(() => {
    if (userLoggedToken) {
      const fetchAllClientInCompleted = async () => {
        const allClients = await fetchAllSubscriptionsComplete()
        setSubscriptions(allClients)
      }
      fetchAllClientInCompleted()
    }
  }, [])

  const checkIfClientExists = (sirenOrTva: string) => {
    try {
      if (/^[0-9]{9}$/.test(sirenOrTva)) {
        return subscriptions.some((sub) => sub.siren === sirenOrTva)
      }
      if (/^BE[0-9]{10}$/.test(sirenOrTva)) {
        return subscriptions.some((sub) => sub.tva === sirenOrTva)
      }
      return false
    } catch (error) {
      return false
    }
  }



  useEffect(() => {

    if (values.tva) {
      const upper = values.tva.toUpperCase()
      if (upper !== values.tva) {
        setFieldValue('tva', upper, false)
        return
      }
    }

    // FR — SIREN
    if (enabled && values.siren && /^[0-9]{9}$/i.test(values.siren)) {
      const exist = checkIfClientExists(values.siren)
      setClientExists(exist)
      if (!exist) {
        loadBusinessData(values.siren, 'FR')
      }
    }

    // BE — TVA
    if (enabled && values.tva && /^BE[0-9]{10}$/i.test(values.tva)) {
      const exist = checkIfClientExists(values.tva)
      setClientExists(exist)
      if (!exist) {
        loadBusinessData(values.tva, 'BE')
      }
    }
  }, [values.siren, values.tva, enabled])


  return {
    pending,
    clientExists,
    errorEllipro,
  }
}

export default useBusinessDataAutoFill
